import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Shield, Users, Building2, Package, BarChart3, Download, 
  RefreshCw, Database, Plus, Search, ExternalLink, Copy,
  Eye, Trash2, UserPlus, BookOpen, Plane, Home, Settings,
  ChevronLeft, ChevronRight, Play
} from 'lucide-react';
import { adminApi } from '@/lib/api';
import { toast } from 'sonner';
import { formatNumber, formatDate, cn } from '@/lib/utils';
import { QRCodeSVG } from 'qrcode.react';

const MODULE_ICONS = {
  notes: BookOpen,
  travel: Plane,
  home: Home,
  car: Settings,
  finance: BarChart3,
};

const MODULE_COLORS = {
  notes: 'text-blue-600 bg-blue-100',
  travel: 'text-teal-600 bg-teal-100',
  home: 'text-emerald-600 bg-emerald-100',
  car: 'text-orange-600 bg-orange-100',
  finance: 'text-violet-600 bg-violet-100',
};

const STATUS_COLORS = {
  generated: 'bg-slate-100 text-slate-600',
  encoded: 'bg-purple-100 text-purple-700',
  distributed: 'bg-blue-100 text-blue-700',
  activated: 'bg-emerald-100 text-emerald-700',
  archived: 'bg-gray-100 text-gray-600'
};

export default function AdminPanel() {
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [users, setUsers] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [demoTokens, setDemoTokens] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  
  // Search and filters
  const [globalSearch, setGlobalSearch] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [tokenSearch, setTokenSearch] = useState('');
  
  // Campaign detail view
  const [viewingCampaign, setViewingCampaign] = useState(null);
  const [campaignTokens, setCampaignTokens] = useState([]);
  const [campaignDetails, setCampaignDetails] = useState(null);
  
  // Dialogs
  const [showCreateCampaign, setShowCreateCampaign] = useState(false);
  const [showGenerateTokens, setShowGenerateTokens] = useState(false);
  const [showAssignUser, setShowAssignUser] = useState(false);
  const [showUpdateStatus, setShowUpdateStatus] = useState(null);
  
  // Form states
  const [campaignName, setCampaignName] = useState('');
  const [campaignSponsor, setCampaignSponsor] = useState('');
  const [campaignArtifactType, setCampaignArtifactType] = useState('notes');
  const [campaignTokenPrefix, setCampaignTokenPrefix] = useState('');
  const [campaignTokenQty, setCampaignTokenQty] = useState('100');
  
  const [generateQty, setGenerateQty] = useState('50');
  const [generatePrefix, setGeneratePrefix] = useState('');
  
  const [assignEmail, setAssignEmail] = useState('');
  
  const [isSeeding, setIsSeeding] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    loadData();
    loadDemoTokens();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, campaignsRes, sponsorsRes, usersRes] = await Promise.all([
        adminApi.getStats(),
        adminApi.getCampaigns(),
        adminApi.getSponsors(),
        adminApi.getUsers()
      ]);
      setStats(statsRes.data);
      setCampaigns(campaignsRes.data);
      setSponsors(sponsorsRes.data);
      setUsers(usersRes.data.users || []);
    } catch (error) {
      toast.error('Failed to load admin data');
    } finally {
      setIsLoading(false);
    }
  };

  const loadDemoTokens = async () => {
    try {
      const response = await adminApi.getDemoTokens();
      setDemoTokens(response.data);
    } catch (error) {
      console.error('Failed to load demo tokens');
    }
  };

  const loadTokens = async () => {
    try {
      const campaignFilter = selectedCampaign === 'all' ? undefined : selectedCampaign;
      const statusFilter = selectedStatus === 'all' ? undefined : selectedStatus;
      const response = await adminApi.getTokens(campaignFilter || undefined, undefined, statusFilter || undefined, 0, 100);
      setTokens(response.data);
    } catch (error) {
      toast.error('Failed to load tokens');
    }
  };

  const loadCampaignDetails = async (campaignId) => {
    try {
      const [detailsRes, tokensRes] = await Promise.all([
        adminApi.getCampaignDetails(campaignId),
        adminApi.getCampaignTokens(campaignId, null, tokenSearch, 0, 100)
      ]);
      setCampaignDetails(detailsRes.data);
      setCampaignTokens(tokensRes.data.tokens || []);
    } catch (error) {
      toast.error('Failed to load campaign details');
    }
  };

  useEffect(() => {
    if (activeTab === 'tokens') {
      loadTokens();
    }
  }, [activeTab, selectedCampaign, selectedStatus]);

  useEffect(() => {
    if (viewingCampaign) {
      loadCampaignDetails(viewingCampaign);
    }
  }, [viewingCampaign, tokenSearch]);

  const handleGlobalSearch = async () => {
    if (!globalSearch.trim()) {
      setSearchResults(null);
      return;
    }
    try {
      const response = await adminApi.globalSearch(globalSearch);
      setSearchResults(response.data);
    } catch (error) {
      toast.error('Search failed');
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      handleGlobalSearch();
    }, 300);
    return () => clearTimeout(timer);
  }, [globalSearch]);

  const handleSeedDemo = async () => {
    setIsSeeding(true);
    try {
      const response = await adminApi.seedDemoData();
      toast.success('Demo data seeded successfully!');
      toast.info(`Sponsor: ${response.data.sponsor_email} / ${response.data.sponsor_password}`);
      loadData();
      loadDemoTokens();
    } catch (error) {
      toast.error('Failed to seed demo data');
    } finally {
      setIsSeeding(false);
    }
  };

  const handleResetDemo = async () => {
    if (!confirm('This will reset all demo data. Continue?')) return;
    
    setIsSeeding(true);
    try {
      await adminApi.resetDemoData();
      toast.success('Demo data reset successfully!');
      loadData();
      loadDemoTokens();
    } catch (error) {
      toast.error('Failed to reset demo data');
    } finally {
      setIsSeeding(false);
    }
  };

  const handleExportTokens = async (campaignId) => {
    try {
      const response = await adminApi.exportTokens(campaignId);
      const blob = new Blob([response.data.csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tokens-${campaignId}.csv`;
      a.click();
      toast.success(`Exported ${response.data.count} tokens`);
    } catch (error) {
      toast.error('Failed to export tokens');
    }
  };

  const handleCreateCampaign = async () => {
    if (!campaignName || !campaignSponsor) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setIsSubmitting(true);
    try {
      await adminApi.createCampaign(
        campaignName,
        campaignSponsor,
        campaignArtifactType,
        campaignTokenPrefix || null,
        parseInt(campaignTokenQty) || 100,
        new Date().toISOString(),
        null
      );
      toast.success(`Campaign created with ${campaignTokenQty} tokens!`);
      setShowCreateCampaign(false);
      setCampaignName('');
      setCampaignSponsor('');
      setCampaignTokenPrefix('');
      setCampaignTokenQty('100');
      loadData();
    } catch (error) {
      toast.error('Failed to create campaign');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGenerateTokens = async () => {
    if (!viewingCampaign) return;
    
    setIsSubmitting(true);
    try {
      await adminApi.generateTokens(
        viewingCampaign,
        parseInt(generateQty) || 50,
        campaignDetails?.campaign?.artifact_type || 'notes',
        generatePrefix || null
      );
      toast.success(`Generated ${generateQty} tokens!`);
      setShowGenerateTokens(false);
      setGenerateQty('50');
      setGeneratePrefix('');
      loadCampaignDetails(viewingCampaign);
      loadData();
    } catch (error) {
      toast.error('Failed to generate tokens');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAssignUser = async () => {
    if (!viewingCampaign || !assignEmail) return;
    
    setIsSubmitting(true);
    try {
      const response = await adminApi.assignUserToCampaign(viewingCampaign, assignEmail);
      toast.success(`User assigned to token ${response.data.token_id}`);
      setShowAssignUser(false);
      setAssignEmail('');
      loadCampaignDetails(viewingCampaign);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign user');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateStatus = async (tokenId, newStatus) => {
    try {
      await adminApi.updateTokenStatus(tokenId, newStatus);
      toast.success('Status updated');
      setShowUpdateStatus(null);
      if (viewingCampaign) {
        loadCampaignDetails(viewingCampaign);
      }
      loadTokens();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDeleteCampaign = async (campaignId) => {
    if (!confirm('Delete this campaign and all its tokens? This cannot be undone.')) return;
    
    try {
      await adminApi.deleteCampaign(campaignId);
      toast.success('Campaign deleted');
      setViewingCampaign(null);
      loadData();
    } catch (error) {
      toast.error('Failed to delete campaign');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const getTokenUrl = (token) => {
    const prefix = { notes: 'n', travel: 't', home: 'h' }[token.module_type] || 'n';
    return `${window.location.origin}/${prefix}/${token.token_id}`;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-slate-900 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  // Campaign Detail View
  if (viewingCampaign && campaignDetails) {
    const Icon = MODULE_ICONS[campaignDetails.campaign?.artifact_type] || Package;
    
    return (
      <div className="min-h-screen bg-slate-50" data-testid="admin-campaign-detail">
        {/* Header */}
        <header className="bg-slate-900 text-white">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => setViewingCampaign(null)}
                className="text-slate-400 hover:text-white transition-colors"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <div className="flex items-center gap-3">
                <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center", MODULE_COLORS[campaignDetails.campaign?.artifact_type])}>
                  <Icon className="w-5 h-5" />
                </div>
                <div>
                  <h1 className="font-bold text-lg">{campaignDetails.campaign?.name}</h1>
                  <p className="text-xs text-slate-400">{campaignDetails.sponsor?.company_name}</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowAssignUser(true)}
                className="rounded-full border-slate-600 text-white hover:bg-slate-800"
                data-testid="assign-user-btn"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Assign User
              </Button>
              <Button 
                size="sm"
                onClick={() => setShowGenerateTokens(true)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full"
                data-testid="generate-tokens-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Generate Tokens
              </Button>
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-6 py-8">
          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-xl p-4 border border-slate-100">
              <p className="text-2xl font-bold text-slate-900">{formatNumber(campaignDetails.stats?.total_tokens || 0)}</p>
              <p className="text-sm text-slate-500">Total Tokens</p>
            </div>
            <div className="bg-white rounded-xl p-4 border border-slate-100">
              <p className="text-2xl font-bold text-emerald-600">{formatNumber(campaignDetails.stats?.activated_tokens || 0)}</p>
              <p className="text-sm text-slate-500">Activated</p>
            </div>
            <div className="bg-white rounded-xl p-4 border border-slate-100">
              <p className="text-2xl font-bold text-blue-600">{campaignDetails.stats?.activation_rate || 0}%</p>
              <p className="text-sm text-slate-500">Activation Rate</p>
            </div>
            <div className="bg-white rounded-xl p-4 border border-slate-100">
              <p className="text-2xl font-bold text-slate-900">{formatNumber(campaignDetails.stats?.notes_uploaded || 0)}</p>
              <p className="text-sm text-slate-500">Notes Uploaded</p>
            </div>
            <div className="bg-white rounded-xl p-4 border border-slate-100">
              <p className="text-2xl font-bold text-slate-900">{formatNumber(campaignDetails.stats?.documents_uploaded || 0)}</p>
              <p className="text-sm text-slate-500">Docs Uploaded</p>
            </div>
          </div>

          {/* Token Search */}
          <div className="flex items-center gap-4 mb-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search tokens..."
                value={tokenSearch}
                onChange={(e) => setTokenSearch(e.target.value)}
                className="pl-10 bg-white border-slate-200 rounded-xl"
                data-testid="token-search-input"
              />
            </div>
            <Button 
              variant="outline"
              onClick={() => handleExportTokens(viewingCampaign)}
              className="rounded-full"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
            <Button 
              variant="destructive"
              onClick={() => handleDeleteCampaign(viewingCampaign)}
              className="rounded-full"
              data-testid="delete-campaign-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          </div>

          {/* Tokens Table */}
          <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Token ID</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">QR</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Module</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Status</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Linked User</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Remarks</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Activated</th>
                    <th className="text-right px-4 py-3 text-sm font-medium text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {campaignTokens.map((token) => {
                    const TokenIcon = MODULE_ICONS[token.module_type] || Package;
                    return (
                      <tr key={token.id} className="hover:bg-slate-50" data-testid={`token-row-${token.token_id}`}>
                        <td className="px-4 py-3">
                          <span className="font-mono text-sm text-slate-900">{token.token_id}</span>
                        </td>
                        <td className="px-4 py-3">
                          <QRCodeSVG value={`${window.location.origin}/${({notes:'n',travel:'t',home:'h',car:'c',finance:'f'})[token.module_type] || 'n'}/${token.token_id}`} size={28} level="M" />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className={cn("w-6 h-6 rounded flex items-center justify-center", MODULE_COLORS[token.module_type])}>
                              <TokenIcon className="w-3.5 h-3.5" />
                            </div>
                            <span className="text-sm capitalize">{token.module_type}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => setShowUpdateStatus(token)}
                            className={cn("px-2 py-1 rounded-full text-xs font-medium", STATUS_COLORS[token.status])}
                          >
                            {token.status}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">
                          {token.linked_user?.email || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-500 max-w-[150px]">
                          <span className="truncate block">{token.remarks || '-'}</span>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-500">
                          {token.activated_at ? formatDate(token.activated_at) : '-'}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => copyToClipboard(getTokenUrl(token))}
                              className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                              title="Copy URL"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                            <a
                              href={getTokenUrl(token)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Test Token"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {campaignTokens.length === 0 && (
              <div className="p-8 text-center text-slate-400">
                No tokens found
              </div>
            )}
          </div>
        </main>

        {/* Generate Tokens Dialog */}
        <Dialog open={showGenerateTokens} onOpenChange={setShowGenerateTokens}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Generate More Tokens</DialogTitle>
              <DialogDescription>Add more tokens to this campaign</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">Quantity</label>
                <Input
                  type="number"
                  value={generateQty}
                  onChange={(e) => setGenerateQty(e.target.value)}
                  className="bg-slate-50 border-slate-200 rounded-xl"
                  data-testid="generate-qty-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">Prefix (optional)</label>
                <Input
                  placeholder="TOY"
                  value={generatePrefix}
                  onChange={(e) => setGeneratePrefix(e.target.value.toUpperCase())}
                  className="bg-slate-50 border-slate-200 rounded-xl"
                  data-testid="generate-prefix-input"
                />
              </div>
              <Button
                onClick={handleGenerateTokens}
                disabled={isSubmitting}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-full"
                data-testid="confirm-generate-btn"
              >
                {isSubmitting ? 'Generating...' : `Generate ${generateQty} Tokens`}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Assign User Dialog */}
        <Dialog open={showAssignUser} onOpenChange={setShowAssignUser}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Assign User to Campaign</DialogTitle>
              <DialogDescription>Link a user to an available token</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">User Email</label>
                <Input
                  type="email"
                  placeholder="user@example.com"
                  value={assignEmail}
                  onChange={(e) => setAssignEmail(e.target.value)}
                  className="bg-slate-50 border-slate-200 rounded-xl"
                  data-testid="assign-email-input"
                />
              </div>
              <Button
                onClick={handleAssignUser}
                disabled={isSubmitting || !assignEmail}
                className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-full"
                data-testid="confirm-assign-btn"
              >
                {isSubmitting ? 'Assigning...' : 'Assign User'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Update Status Dialog */}
        <Dialog open={!!showUpdateStatus} onOpenChange={() => setShowUpdateStatus(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Update Token Status</DialogTitle>
              <DialogDescription>Token: {showUpdateStatus?.token_id}</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-2 mt-4">
              {['generated', 'encoded', 'distributed', 'activated', 'archived'].map((status) => (
                <button
                  key={status}
                  onClick={() => handleUpdateStatus(showUpdateStatus?.token_id, status)}
                  className={cn(
                    "px-4 py-3 rounded-xl text-sm font-medium capitalize transition-all",
                    showUpdateStatus?.status === status 
                      ? "ring-2 ring-slate-900 ring-offset-2" 
                      : "hover:ring-2 hover:ring-slate-300",
                    STATUS_COLORS[status]
                  )}
                >
                  {status}
                </button>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Main Admin Panel
  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-panel">
      {/* Header */}
      <header className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center">
              <Shield className="w-5 h-5" />
            </div>
            <div>
              <h1 className="font-bold text-lg">MEMORA Admin</h1>
              <p className="text-xs text-slate-400">Platform Management</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Global Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search tokens, campaigns, users..."
                value={globalSearch}
                onChange={(e) => setGlobalSearch(e.target.value)}
                className="pl-10 w-64 bg-slate-800 border-slate-700 text-white placeholder:text-slate-400 rounded-xl"
                data-testid="global-search-input"
              />
              {searchResults && globalSearch && (
                <div className="absolute top-full mt-2 left-0 right-0 bg-white rounded-xl shadow-xl border border-slate-200 p-4 z-50 max-h-96 overflow-auto">
                  {searchResults.tokens?.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs font-medium text-slate-500 mb-2">TOKENS</p>
                      {searchResults.tokens.map(t => (
                        <div key={t.id} className="flex items-center justify-between py-2 hover:bg-slate-50 rounded px-2">
                          <span className="font-mono text-sm text-slate-700">{t.token_id}</span>
                          <span className={cn("px-2 py-0.5 rounded-full text-xs", STATUS_COLORS[t.status])}>{t.status}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {searchResults.campaigns?.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs font-medium text-slate-500 mb-2">CAMPAIGNS</p>
                      {searchResults.campaigns.map(c => (
                        <button key={c.id} onClick={() => { setViewingCampaign(c.id); setGlobalSearch(''); }} className="w-full text-left py-2 hover:bg-slate-50 rounded px-2">
                          <span className="text-sm text-slate-700">{c.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {searchResults.users?.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-slate-500 mb-2">USERS</p>
                      {searchResults.users.map(u => (
                        <div key={u.id} className="py-2 hover:bg-slate-50 rounded px-2">
                          <span className="text-sm text-slate-700">{u.email}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {!searchResults.tokens?.length && !searchResults.campaigns?.length && !searchResults.users?.length && (
                    <p className="text-sm text-slate-400 text-center py-4">No results found</p>
                  )}
                </div>
              )}
            </div>
            <Link to="/" className="text-slate-400 hover:text-white transition-colors">
              Back to Site
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-8">
          <TabsList className="bg-white border border-slate-100 rounded-full p-1 inline-flex flex-wrap">
            <TabsTrigger value="overview" className="rounded-full px-4 data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="tab-overview">
              Overview
            </TabsTrigger>
            <TabsTrigger value="campaigns" className="rounded-full px-4 data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="tab-campaigns">
              Campaigns
            </TabsTrigger>
            <TabsTrigger value="sponsors" className="rounded-full px-4 data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="tab-sponsors">
              Sponsors
            </TabsTrigger>
            <TabsTrigger value="tokens" className="rounded-full px-4 data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="tab-tokens">
              Tokens
            </TabsTrigger>
            <TabsTrigger value="demo" className="rounded-full px-4 data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="tab-demo">
              Demo & Quick Access
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl p-6 border border-slate-100">
                <Users className="w-8 h-8 text-blue-500 mb-3" />
                <p className="text-3xl font-bold text-slate-900">{formatNumber(stats?.total_users || 0)}</p>
                <p className="text-sm text-slate-500">Total Users</p>
              </motion.div>
              
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white rounded-2xl p-6 border border-slate-100">
                <Building2 className="w-8 h-8 text-amber-500 mb-3" />
                <p className="text-3xl font-bold text-slate-900">{formatNumber(stats?.total_sponsors || 0)}</p>
                <p className="text-sm text-slate-500">Sponsors</p>
              </motion.div>
              
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white rounded-2xl p-6 border border-slate-100">
                <BarChart3 className="w-8 h-8 text-emerald-500 mb-3" />
                <p className="text-3xl font-bold text-slate-900">{formatNumber(stats?.total_campaigns || 0)}</p>
                <p className="text-sm text-slate-500">Campaigns</p>
              </motion.div>
              
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="bg-white rounded-2xl p-6 border border-slate-100">
                <Package className="w-8 h-8 text-purple-500 mb-3" />
                <p className="text-3xl font-bold text-slate-900">{formatNumber(stats?.total_tokens || 0)}</p>
                <p className="text-sm text-slate-500">Total Tokens</p>
                <p className="text-xs text-emerald-600 mt-1">{formatNumber(stats?.activated_tokens || 0)} activated</p>
              </motion.div>
            </div>

            <div className="grid md:grid-cols-2 gap-4 mt-6">
              <div className="bg-white rounded-2xl p-6 border border-slate-100">
                <h3 className="font-semibold text-slate-900 mb-4">Content Stats</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Notes Uploaded</span>
                    <span className="font-semibold text-slate-900">{formatNumber(stats?.total_notes || 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Travel Documents</span>
                    <span className="font-semibold text-slate-900">{formatNumber(stats?.total_documents || 0)}</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-2xl p-6 border border-slate-100">
                <h3 className="font-semibold text-slate-900 mb-4">Token Activation</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Activation Rate</span>
                    <span className="font-semibold text-emerald-600">
                      {stats?.total_tokens ? ((stats.activated_tokens / stats.total_tokens) * 100).toFixed(1) : 0}%
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Pending Tokens</span>
                    <span className="font-semibold text-slate-900">
                      {formatNumber((stats?.total_tokens || 0) - (stats?.activated_tokens || 0))}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Campaigns Tab */}
          <TabsContent value="campaigns">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-slate-900">Campaign Management</h2>
              <Button 
                onClick={() => setShowCreateCampaign(true)}
                className="bg-slate-900 hover:bg-slate-800 text-white rounded-full"
                data-testid="create-campaign-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Campaign
              </Button>
            </div>

            <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-100">
                    <tr>
                      <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Campaign</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Sponsor</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Type</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Tokens</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Activation</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Created</th>
                      <th className="text-right px-4 py-3 text-sm font-medium text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {campaigns.map((campaign) => {
                      const sponsor = sponsors.find(s => s.id === campaign.sponsor_id);
                      const Icon = MODULE_ICONS[campaign.artifact_type] || Package;
                      return (
                        <tr key={campaign.id} className="hover:bg-slate-50" data-testid={`campaign-row-${campaign.id}`}>
                          <td className="px-4 py-3">
                            <p className="font-medium text-slate-900">{campaign.name}</p>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600">
                            {sponsor?.company_name || '-'}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className={cn("w-6 h-6 rounded flex items-center justify-center", MODULE_COLORS[campaign.artifact_type])}>
                                <Icon className="w-3.5 h-3.5" />
                              </div>
                              <span className="text-sm capitalize">{campaign.artifact_type}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600">-</td>
                          <td className="px-4 py-3 text-sm text-slate-600">-</td>
                          <td className="px-4 py-3 text-sm text-slate-500">{formatDate(campaign.start_date)}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setViewingCampaign(campaign.id)}
                                data-testid={`view-campaign-${campaign.id}`}
                              >
                                <Eye className="w-4 h-4 mr-1" />
                                View
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleExportTokens(campaign.id)}
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {campaigns.length === 0 && (
                <div className="p-8 text-center text-slate-400">No campaigns yet</div>
              )}
            </div>
          </TabsContent>

          {/* Sponsors Tab */}
          <TabsContent value="sponsors">
            <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
              <div className="p-4 border-b border-slate-100">
                <h3 className="font-semibold text-slate-900">All Sponsors ({sponsors.length})</h3>
              </div>
              {sponsors.length === 0 ? (
                <div className="p-8 text-center text-slate-400">No sponsors yet</div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {sponsors.map((sponsor) => (
                    <div key={sponsor.id} className="p-4 flex items-center justify-between" data-testid={`admin-sponsor-${sponsor.id}`}>
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{sponsor.company_name}</p>
                          <p className="text-sm text-slate-500">{sponsor.email}</p>
                        </div>
                      </div>
                      <span className="px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-sm capitalize">
                        {sponsor.plan_type}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Tokens Tab */}
          <TabsContent value="tokens">
            <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
              <div className="p-4 border-b border-slate-100 flex items-center gap-4">
                <Select value={selectedCampaign} onValueChange={setSelectedCampaign}>
                  <SelectTrigger className="w-48 rounded-xl" data-testid="campaign-filter">
                    <SelectValue placeholder="All Campaigns" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Campaigns</SelectItem>
                    {campaigns.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                  <SelectTrigger className="w-40 rounded-xl" data-testid="status-filter">
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="generated">Generated</SelectItem>
                    <SelectItem value="encoded">Encoded</SelectItem>
                    <SelectItem value="distributed">Distributed</SelectItem>
                    <SelectItem value="activated">Activated</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {tokens.length === 0 ? (
                <div className="p-8 text-center text-slate-400">No tokens found</div>
              ) : (
                <div className="divide-y divide-slate-100 max-h-96 overflow-auto">
                  {tokens.map((token) => {
                    const TokenIcon = MODULE_ICONS[token.module_type] || Package;
                    return (
                      <div key={token.id} className="p-4 flex items-center justify-between" data-testid={`token-${token.token_id}`}>
                        <div className="flex items-center gap-4">
                          <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center", MODULE_COLORS[token.module_type])}>
                            <TokenIcon className="w-4 h-4" />
                          </div>
                          <div>
                            <p className="font-mono text-slate-900">{token.token_id}</p>
                            <p className="text-xs text-slate-500 capitalize">{token.module_type}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={cn("px-3 py-1 rounded-full text-xs font-medium", STATUS_COLORS[token.status])}>
                            {token.status}
                          </span>
                          <button
                            onClick={() => copyToClipboard(getTokenUrl(token))}
                            className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg"
                            title="Copy URL"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                          <a
                            href={getTokenUrl(token)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                            title="Test"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Demo & Quick Access Tab */}
          <TabsContent value="demo">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Demo Token Launcher */}
              <div className="bg-white rounded-2xl border border-slate-100 p-6">
                <div className="flex items-center gap-3 mb-6">
                  <Play className="w-8 h-8 text-emerald-500" />
                  <div>
                    <h3 className="font-semibold text-slate-900">Demo Token Launcher</h3>
                    <p className="text-sm text-slate-500">Quick access for presentations</p>
                  </div>
                </div>
                
                <div className="space-y-3">
                  {Object.entries(MODULE_ICONS).map(([moduleType, Icon]) => {
                    const token = demoTokens[moduleType];
                    const colors = MODULE_COLORS[moduleType];
                    return (
                      <div key={moduleType} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                        <div className="flex items-center gap-3">
                          <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center", colors)}>
                            <Icon className="w-5 h-5" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900 capitalize">Memora {moduleType}</p>
                            {token ? (
                              <p className="text-xs font-mono text-slate-500">{token.token_id}</p>
                            ) : (
                              <p className="text-xs text-slate-400">No demo token</p>
                            )}
                          </div>
                        </div>
                        {token && (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => copyToClipboard(`${window.location.origin}${token.url}`)}
                              className="p-2 text-slate-400 hover:text-slate-600 hover:bg-white rounded-lg"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                            <a
                              href={token.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-4 py-2 bg-slate-900 text-white rounded-full text-sm font-medium hover:bg-slate-800"
                              data-testid={`demo-${moduleType}-btn`}
                            >
                              Test
                            </a>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Demo Data Management */}
              <div className="bg-white rounded-2xl border border-slate-100 p-6">
                <div className="flex items-center gap-3 mb-6">
                  <Database className="w-8 h-8 text-slate-400" />
                  <div>
                    <h3 className="font-semibold text-slate-900">Demo Data Management</h3>
                    <p className="text-sm text-slate-500">Seed or reset demo data</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="p-4 bg-slate-50 rounded-xl">
                    <h4 className="font-medium text-slate-900 mb-2">Seed Demo Data</h4>
                    <p className="text-sm text-slate-600 mb-4">
                      Creates Toyota demo campaign with tokens, user with artifacts, and sample data.
                    </p>
                    <Button
                      onClick={handleSeedDemo}
                      disabled={isSeeding}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full"
                      data-testid="seed-demo-btn"
                    >
                      {isSeeding ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Seeding...</> : <><Database className="w-4 h-4 mr-2" />Seed Demo Data</>}
                    </Button>
                  </div>
                  
                  <div className="p-4 bg-red-50 rounded-xl">
                    <h4 className="font-medium text-red-900 mb-2">Reset Demo Data</h4>
                    <p className="text-sm text-red-700 mb-4">
                      Clears and reseeds fresh data. Use before presentations.
                    </p>
                    <Button
                      onClick={handleResetDemo}
                      disabled={isSeeding}
                      variant="destructive"
                      className="rounded-full"
                      data-testid="reset-demo-btn"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Reset Demo Data
                    </Button>
                  </div>
                </div>
                
                <div className="mt-6 p-4 bg-amber-50 rounded-xl">
                  <h4 className="font-medium text-amber-900 mb-2">Demo Credentials</h4>
                  <div className="text-sm text-amber-800 space-y-1">
                    <p><strong>Sponsor:</strong> demo@toyota.com / demo123</p>
                    <p><strong>User:</strong> demo@memora.app (magic link)</p>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Create Campaign Dialog */}
      <Dialog open={showCreateCampaign} onOpenChange={setShowCreateCampaign}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Create New Campaign</DialogTitle>
            <DialogDescription>Set up a new campaign with auto-generated tokens</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Campaign Name *</label>
              <Input
                placeholder="Toyota Smart Notebook 2026"
                value={campaignName}
                onChange={(e) => setCampaignName(e.target.value)}
                className="bg-slate-50 border-slate-200 rounded-xl"
                data-testid="campaign-name-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Sponsor *</label>
              <Select value={campaignSponsor} onValueChange={setCampaignSponsor}>
                <SelectTrigger className="rounded-xl" data-testid="campaign-sponsor-select">
                  <SelectValue placeholder="Select sponsor" />
                </SelectTrigger>
                <SelectContent>
                  {sponsors.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.company_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Artifact Type</label>
              <Select value={campaignArtifactType} onValueChange={setCampaignArtifactType}>
                <SelectTrigger className="rounded-xl" data-testid="campaign-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="notes">Memora Notes</SelectItem>
                  <SelectItem value="travel">Memora Travel Tag</SelectItem>
                  <SelectItem value="home">Memora Home Hub</SelectItem>
                  <SelectItem value="car">Memora Car Diary</SelectItem>
                  <SelectItem value="finance">Memora Wealth Compass</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">Token Prefix</label>
                <Input
                  placeholder="TOY"
                  value={campaignTokenPrefix}
                  onChange={(e) => setCampaignTokenPrefix(e.target.value.toUpperCase())}
                  className="bg-slate-50 border-slate-200 rounded-xl"
                  data-testid="campaign-prefix-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">Token Quantity</label>
                <Input
                  type="number"
                  value={campaignTokenQty}
                  onChange={(e) => setCampaignTokenQty(e.target.value)}
                  className="bg-slate-50 border-slate-200 rounded-xl"
                  data-testid="campaign-qty-input"
                />
              </div>
            </div>
            <Button
              onClick={handleCreateCampaign}
              disabled={isSubmitting || !campaignName || !campaignSponsor}
              className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-full"
              data-testid="confirm-create-campaign-btn"
            >
              {isSubmitting ? 'Creating...' : `Create Campaign with ${campaignTokenQty} Tokens`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
